import type { Chat } from '@ai-sdk/react';
import type { CrowdexUIMessage } from '@crowdex/agent-contracts';
import { apiFetch } from '@crowdex/host-kit/renderer/http';
import { useEffect } from 'react';
import { onStreamedEvent } from '@/features/notifications/notification-feed';
import { readConversationPullRequestBindings } from '@/features/pull-request/conversation-pull-requests';
import type { DesktopShellStore } from '@/lib/desktop-shell/store';
import { getLocalConversation } from '@/lib/local-conversations';
import { joinSidebar } from './joined-conversations';

/**
 * The core's event of a conversation that changed without the page: the server started a turn in
 * it (events woke it, an app or the assistant sent it work) or added messages (a call's words).
 */
export const CHANGED_EVENT = 'crowdex/conversation.changed';

/** The conversation's transcript as its server keeps it (what the server added to it). */
async function serverMessages(
  store: DesktopShellStore,
  threadId: string,
): Promise<CrowdexUIMessage[] | null> {
  const stored = store.getState().syncedThreads.find((thread) => thread.id === threadId);
  if (stored?.runtime !== 'cloud') {
    const local = await getLocalConversation(threadId);
    if (local !== null) return [...local.messages] as CrowdexUIMessage[];
  }
  const saved = await apiFetch<{ messages: CrowdexUIMessage[] }>(
    `/api/sync/messages?threadId=${encodeURIComponent(threadId)}`,
    { credentials: 'include' },
  );
  return saved.messages;
}

/**
 * A conversation that changed without the page shows it: one the sidebar did not list joins it,
 * and one open here reads its transcript again, then follows the turn the server runs in it (the
 * conversation shown gets a new chat, which does on mount; one of a split resumes its own).
 */
export function useConversationChanges(
  store: DesktopShellStore,
  chats: { readonly current: Map<string, Chat<CrowdexUIMessage>> },
): void {
  useEffect(
    () =>
      onStreamedEvent((event) => {
        if (event.type !== CHANGED_EVENT || event.threadId === null) return;
        const threadId = event.threadId;
        // Moved into or out of a project: the directory's own event brings where.
        if (event.fields['change'] === 'moved') return;
        if (event.fields['change'] === 'pull-request') {
          // Its pull requests changed, not its branch: the sidebar takes them again.
          if (event.fields['bindings'] !== undefined) {
            void readConversationPullRequestBindings(threadId).then((bindings) => {
              if (bindings === null) return;
              store
                .getState()
                .setSyncedThreads((current) =>
                  current.map((thread) =>
                    thread.id === threadId ? { ...thread, pullRequestBindings: bindings } : thread,
                  ),
                );
            });
            return;
          }
          const branch = event.fields['branch'];
          const url = event.fields['url'];
          const claim =
            typeof branch === 'string'
              ? { branch, url: typeof url === 'string' ? url : null }
              : null;
          store
            .getState()
            .setSyncedThreads((current) =>
              current.map((thread) =>
                thread.id === threadId ? { ...thread, pullRequest: claim } : thread,
              ),
            );
          return;
        }
        if (event.fields['change'] === 'renamed' || event.fields['change'] === 'archived') {
          const archived = event.fields['change'] === 'archived';
          const state = store.getState();
          // The conversation shown takes its title from the shell, and its cache when back to it.
          if (!archived && threadId === state.chatId)
            state.setGeneratedConversationTitle(event.title);
          // An archived one leaves the cache too, whose opened conversations the sidebar lists.
          state.setConversationCache((cache) => {
            const conversation = cache[threadId];
            if (conversation === undefined) return cache;
            if (!archived) return { ...cache, [threadId]: { ...conversation, title: event.title } };
            const { [threadId]: _archived, ...rest } = cache;
            return rest;
          });
          state.setSyncedThreads((current) =>
            archived
              ? current.filter((thread) => thread.id !== threadId)
              : current.map((thread) =>
                  thread.id === threadId ? { ...thread, title: event.title } : thread,
                ),
          );
          return;
        }
        // Started without the page: it joins the sidebar.
        void joinSidebar(store, threadId);
        const chat = chats.current.get(threadId);
        // Not open here, or already following a turn of its own.
        if (chat === undefined || chat.status === 'submitted' || chat.status === 'streaming')
          return;
        void serverMessages(store, threadId)
          .then(async (messages) => {
            if (messages === null || chat.status === 'submitted' || chat.status === 'streaming')
              return;
            const state = store.getState();
            // Coming back to it later restores what the server keeps, not what was shown.
            state.setConversationCache((cache) => {
              const conversation = cache[threadId];
              return conversation === undefined
                ? cache
                : { ...cache, [threadId]: { ...conversation, messages, updatedAt: Date.now() } };
            });
            if (threadId === state.chatId) {
              // As a handoff does: a new chat from what the server keeps, which follows the turn
              // when it mounts. Resuming this one instead races the shell replacing it (its
              // transport changes while it is idle), and the page stops showing the turn.
              chats.current.delete(threadId);
              state.setInitialMessages(threadId, messages);
              return;
            }
            chat.messages = messages;
            await chat.resumeStream();
          })
          .catch(() => undefined);
      }),
    [chats, store],
  );
}
